import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { buildRenderDocument } from './mediaCreatorRenderDocument';

const VIEWPORT_PAD_PX = 56;

function fitScaleForViewport(viewportW, viewportH, artW, artH) {
  const innerW = Math.max(200, viewportW - VIEWPORT_PAD_PX * 2);
  const innerH = Math.max(200, viewportH - VIEWPORT_PAD_PX * 2);
  return Math.min(innerW / artW, innerH / artH);
}

/** Fullscreen preview of the exact render document used for HTML download and PNG export. */
export default function MediaCreatorFullscreenViewer({ open, onClose, layout, assetUrls }) {
  const artW = layout?.size_px?.width || 1920;
  const artH = layout?.size_px?.height || 1080;
  const [viewport, setViewport] = useState(() => ({
    width: typeof window !== 'undefined' ? window.innerWidth : artW,
    height: typeof window !== 'undefined' ? window.innerHeight : artH,
  }));

  useEffect(() => {
    if (!open) return undefined;
    const onResize = () => {
      setViewport({ width: window.innerWidth, height: window.innerHeight });
    };
    window.addEventListener('resize', onResize);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('resize', onResize);
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const scale = useMemo(
    () => fitScaleForViewport(viewport.width, viewport.height, artW, artH),
    [viewport.width, viewport.height, artW, artH],
  );

  const srcDoc = useMemo(
    () => (open && layout
      ? buildRenderDocument(layout, { assetUrls, bodyBackground: 'transparent' })
      : ''),
    [open, layout, assetUrls],
  );

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="mc-viewer-fullscreen"
      role="dialog"
      aria-modal="true"
      aria-label="Fullscreen design preview"
    >
      <button
        type="button"
        className="mc-viewer-fullscreen-close"
        onClick={onClose}
        aria-label="Close fullscreen"
      >
        Close
      </button>
      <div className="mc-viewer-fullscreen-stage">
        <div
          className="mc-viewer-fullscreen-frame"
          style={{ width: artW * scale, height: artH * scale }}
        >
          <iframe
            title="Design preview"
            srcDoc={srcDoc}
            scrolling="no"
            style={{
              width: artW,
              height: artH,
              border: 0,
              transform: `scale(${scale})`,
              transformOrigin: 'top left',
              display: 'block',
            }}
          />
        </div>
      </div>
    </div>,
    document.body,
  );
}
